import { useEffect, useMemo } from "react";
import { Bold } from "@tiptap/extension-bold";
import { Document } from "@tiptap/extension-document";
import { HardBreak } from "@tiptap/extension-hard-break";
import { History } from "@tiptap/extension-history";
import { Italic } from "@tiptap/extension-italic";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Text } from "@tiptap/extension-text";
import { Underline } from "@tiptap/extension-underline";
import type { EditorEvents } from "@tiptap/react";
import { EditorContent, useEditor } from "@tiptap/react";
import {
  isRichTextContent,
  sanitizeTipTapContent,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/richTextHelpers";
import { type TipTapContent } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textTypes";
import { CustomizedLink } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/CustomizedLink";
import { MenuBar } from "./MenuBar";

export const getBuilderTiptapExtensions = (placeholder?: string) => [
  Document,
  Paragraph,
  Text,
  Bold,
  Italic,
  Underline,
  HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          const editor = this.editor;
          editor.commands.setHardBreak();
          return true;
        },
      };
    },
  }),
  CustomizedLink.configure({
    openOnClick: false,
    autolink: false,
    HTMLAttributes: {
      class: "wb-text-link wb-underline",
      style: "font-size: inherit; font-weight: inherit; font-style: inherit;",
    },
  }),
  History,
  ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
];

export interface TipTapEditorProps {
  onBlur?: (props: EditorEvents["blur"]) => void;
  onChange?: (content: TipTapContent) => void;
  onDoubleClick?: () => void;
  placeholder?: string;
  readOnly?: boolean;
  shouldFocusWhenReady?: boolean;
  shouldShowMenuBar?: boolean;
  value?: string | TipTapContent;
}

export const TipTapEditor = ({
  value,
  onChange,
  onBlur,
  placeholder = "Enter text...",
  readOnly = false,
  shouldShowMenuBar = false,
  shouldFocusWhenReady = false,
}: TipTapEditorProps) => {
  const extensions = useMemo(
    () => getBuilderTiptapExtensions(placeholder),
    [placeholder],
  );

  const editor = useEditor(
    {
      extensions,
      content: isRichTextContent(value) ? sanitizeTipTapContent(value) : value,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        if (!readOnly && onChange) {
          const json = editor.getJSON() as TipTapContent;
          const sanitizedJson = isRichTextContent(json)
            ? sanitizeTipTapContent(json)
            : json;
          onChange(sanitizedJson);
        }
      },
      onBlur,
      editorProps: {
        attributes: {
          class: "focus:wb-outline-none",
        },
      },
    },
    [readOnly],
  );

  // Only update editor content when it's first created or when value changes externally
  // (not from typing). This prevents cursor reset on every keystroke.
  useEffect(() => {
    if (editor && !editor.isFocused) {
      const contentToSet = isRichTextContent(value)
        ? sanitizeTipTapContent(value)
        : value;
      editor.commands.setContent(contentToSet);
      if (shouldFocusWhenReady) {
        editor.commands.selectAll();
        editor.commands.focus();
      }
    }
  }, [editor, value, shouldFocusWhenReady]);

  if (!shouldShowMenuBar) {
    return (
      <EditorContent editor={editor} data-testid="builder-tiptap-editor" />
    );
  }

  return (
    <div className="min-h-[100px] rounded-lg border p-3 pt-0">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} data-testid="builder-tiptap-editor" />
    </div>
  );
};
